import React, { useEffect, useState } from "react";
import { Download, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { activateWaitingWorker, isStandalone } from "@/lib/pwa";

const DISMISS_KEY = "pwa.install_prompt_dismissed_until";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Single component renders TWO independent banners:
 *   1. Install prompt   — "beforeinstallprompt" caught + 7-day dismiss
 *   2. Update available — fired by registerServiceWorker on a new SW install
 *
 * Both sit in the bottom-right and self-hide when not relevant.
 */
export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil > Date.now()) return;

    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    }
    function onInstalled() {
      setShowInstall(false);
      setDeferredPrompt(null);
      // Clear the dismiss key so the next reinstall lifecycle works.
      try { localStorage.removeItem(DISMISS_KEY); } catch (e) { /* */ }
    }
    function onUpdateAvailable() {
      setShowUpdate(true);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("pwa:update-available", onUpdateAvailable);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("pwa:update-available", onUpdateAvailable);
    };
  }, []);

  const onInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (e) { /* */ }
    setShowInstall(false);
    setDeferredPrompt(null);
  };

  const onDismissInstall = () => {
    setShowInstall(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    } catch (e) { /* */ }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-[calc(100vw-2rem)] sm:max-w-sm pointer-events-none">
      {showUpdate && (
        <div
          className="pointer-events-auto bg-white border border-brand-200 rounded-xl shadow-lg p-3 flex items-start gap-3 animate-in slide-in-from-bottom-2"
          data-testid="pwa-update-banner"
        >
          <div className="grid place-items-center w-9 h-9 rounded-lg bg-brand-50 text-brand shrink-0">
            <RefreshCw className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-900">A new version is available</div>
            <p className="text-xs text-zinc-500 mt-0.5">Reload to use the latest improvements.</p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                onClick={() => activateWaitingWorker()}
                className="bg-brand hover:bg-brand-dark text-white min-h-9"
                data-testid="pwa-update-reload"
              >
                Reload
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowUpdate(false)}
                className="text-zinc-500"
                data-testid="pwa-update-dismiss"
              >
                Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {showInstall && (
        <div
          className="pointer-events-auto bg-white border border-zinc-200 rounded-xl shadow-lg p-3 flex items-start gap-3 animate-in slide-in-from-bottom-2"
          data-testid="pwa-install-banner"
        >
          <div className="grid place-items-center w-9 h-9 rounded-lg bg-brand-50 text-brand shrink-0">
            <Download className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-900">Install the Hub</div>
            <p className="text-xs text-zinc-500 mt-0.5">
              Faster access and offline shell support, right from your home screen.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                onClick={onInstallClick}
                className="bg-brand hover:bg-brand-dark text-white min-h-9"
                data-testid="pwa-install-accept"
              >
                Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDismissInstall}
                className="text-zinc-500"
                data-testid="pwa-install-dismiss"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={onDismissInstall}
            className="text-zinc-400 hover:text-zinc-600 -mr-1 -mt-1 p-1 rounded"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
